import React, { FC, useState } from 'react'

import styled from 'styled-components'

import { PrimaryInput } from 'src/Components/PrimaryInput'
import { PrimaryDottedButton } from 'src/Components/Buttons/PrimaryDottedButton'
import {
  getIsSpotifyUrl,
  getSpotifyTrackId,
} from 'src/Redux/spotifyTracks-process/utils'
import { useLazyGetSpotifyTrackByIdQuery } from 'src/Redux/spotifyTracksApi'
import { SEARCH_ERROR_SERVICE_UNAVAILABLE } from 'src/Constants/constants'
import { useUpdatePreReleaseTrackLinkMutation } from 'src/Redux/spotify-campaignApi'

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 24px;
  border-radius: 16px;
  border: 1px solid ${({ theme }) => theme.color.lightGrey};
  background-color: ${({ theme }) => theme.color.white};
  box-shadow: ${({ theme }) => theme.shadow.card};
  width: 100%;
`

const Title = styled.div`
  color: ${({ theme }) => theme.color.darkGrey};
  font-family: 'Segoe UI';
  font-size: 18px;
  font-style: normal;
  font-weight: 600;
  line-height: 150%;
`

const Description = styled.div`
  color: ${({ theme }) => theme.color.darkGrey};
  font-family: 'Segoe UI';
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 150%;
`

const StyledPrimaryDottedButton = styled(PrimaryDottedButton)`
  width: fit-content;
`

const DESCRIPTION_TEXT =
  'Your track has officially been released! Share your Spotify track link so curators can add your new release to their playlists.'

const URL_NOT_VALID_ERROR = 'URL is not valid'
const URL_TEMPORARILY_UNAVAILABLE_ERROR =
  'Search by URL is temporarily unavailable. Please try again later.'
const NO_TRACKS_FOUND_ERROR = 'No tracks were found'
const ARTIST_MISMATCH_ERROR_CODE = 'pre-release-track-artist-mismatch'
const ARTIST_MISMATCH_ERROR =
  "The artist on this Spotify track doesn't match the artist on your campaign. Please check the track link or contact support for help."

interface SpotifyTrackLinkBlockProps {
  campaignId: number
}

export const SpotifyTrackLinkBlock: FC<SpotifyTrackLinkBlockProps> = ({
  campaignId,
}) => {
  const [url, setUrl] = useState('')
  const [error, setError] = useState<string | null>(null)

  const [getTrack, { isLoading: isSearching }] =
    useLazyGetSpotifyTrackByIdQuery()
  const [updateTrackLink, { isLoading: isSubmitting }] =
    useUpdatePreReleaseTrackLinkMutation()

  const isLoading = isSearching || isSubmitting

  const handleSubmit = async (): Promise<void> => {
    setError(null)

    if (!getIsSpotifyUrl(url)) {
      setError(URL_NOT_VALID_ERROR)
      return
    }

    const trackId = getSpotifyTrackId(url)
    if (!trackId) {
      setError(URL_NOT_VALID_ERROR)
      return
    }

    const result = await getTrack(trackId)

    if (result.isError) {
      setError(URL_TEMPORARILY_UNAVAILABLE_ERROR)
      return
    }

    if (!result.data) {
      setError(NO_TRACKS_FOUND_ERROR)
      return
    }

    const track = result.data

    try {
      await updateTrackLink({
        id: String(campaignId),
        artistSpotifyIds: track.artists.map((a) => a.id),
        trackImage: track.trackImage,
        trackLength: track.trackDuration,
        trackName: track.trackName,
        trackSpotifyId: track.id,
        trackUrl: track.resourceSpotifyUrl,
      }).unwrap()
    } catch (err) {
      const mutationError = err as { data?: { message?: string } }
      if (mutationError?.data?.message === ARTIST_MISMATCH_ERROR_CODE) {
        setError(ARTIST_MISMATCH_ERROR)
      } else {
        setError(SEARCH_ERROR_SERVICE_UNAVAILABLE)
      }
    }
  }

  return (
    <Container>
      <Title>Spotify track link</Title>
      <Description>{DESCRIPTION_TEXT}</Description>
      <PrimaryInput
        value={url}
        onChange={(e) => {
          setUrl(e.target.value)
          if (error) {
            setError(null)
          }
        }}
        placeholder='Paste Spotify track URL'
        error={error}
        disabled={isLoading}
      />
      <StyledPrimaryDottedButton
        type='button'
        onClick={handleSubmit}
        disabled={!url || isLoading}
      >
        Submit
      </StyledPrimaryDottedButton>
    </Container>
  )
}
